/**
 * DataStep.tsx
 * Step 1 of the print wizard: upload the Excel workbook, pick the data sheet,
 * choose the grouping column (each group = one document), and optionally join
 * a secondary sheet. Extracted from DocxMarkerWizard to keep that file small.
 */
import { Upload, Layers, ListChecks, X, CheckCircle2, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { FileDropZone } from "@/components/ui/file-drop-zone";
import { SearchableSelect } from "./SearchableSelect";
import { classifySheetRole } from "@/utils/workbookRoles";
import { suggestJoinKeys, counterpartColumn } from "@/utils/sheetJoin";
import { useI18n } from "@/i18n";
import type { ParsedWorkbook } from "@/types/excel";

export const NO_MAPPING = "__none__";

export interface DataStepProps {
  busy: boolean;
  workbook: ParsedWorkbook | null;
  workbookRoleSummary: string | null;
  isAlertDismissed: (key: string) => boolean;
  dismissAlert: (key: string) => void;
  selectedSheet: string;
  setSelectedSheet: (v: string) => void;
  headers: string[];
  rows: Record<string, unknown>[];
  groupingColumns: string[];
  setGroupingColumns: (cols: string[]) => void;
  setGroupSearch: (v: string) => void;
  setPrintFilterMode: (v: boolean) => void;
  primaryHeaders: string[];
  enabledPages: unknown[];
  groups: { groupValue: string; totalItems: number }[];
  lookupSheet: string;
  setLookupSheet: (v: string) => void;
  joinKeyPrimary: string;
  setJoinKeyPrimary: (v: string) => void;
  joinKeySecondary: string;
  setJoinKeySecondary: (v: string) => void;
  secondarySheet: { name: string; headers: string[] } | null;
  joinKeyCandidates: string[];
  joined: { addedColumns: string[] };
  joinWarningMessages: string[];
  onExcelUpload: (file: File) => void;
  onContinue: () => void;
}

export function DataStep(props: DataStepProps) {
  const { t } = useI18n();
  const {
    busy,
    workbook,
    workbookRoleSummary,
    isAlertDismissed,
    dismissAlert,
    selectedSheet,
    setSelectedSheet,
    headers,
    rows,
    groupingColumns,
    setGroupingColumns,
    setGroupSearch,
    setPrintFilterMode,
    primaryHeaders,
    enabledPages,
    groups,
    lookupSheet,
    setLookupSheet,
    joinKeyPrimary,
    setJoinKeyPrimary,
    joinKeySecondary,
    setJoinKeySecondary,
    secondarySheet,
    joinKeyCandidates,
    joined,
    joinWarningMessages,
    onExcelUpload,
    onContinue,
  } = props;

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <Card className="shadow-sm overflow-hidden" data-tour="tour-excel-upload">
        <CardHeader className="space-y-0 border-b bg-muted/30 px-3 py-2.5">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Upload className="h-4 w-4 text-primary" /> {t("dataStep.uploadTitle")}
          </CardTitle>
          <CardDescription className="pt-0.5 text-[11px]">
            {t("dataStep.uploadDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 p-3">
          <FileDropZone
            accept=".xlsx,.xls,.xlsm,.csv"
            onFile={onExcelUpload}
            label={workbook ? t("dataStep.changeFile") : t("dataStep.uploadLabel")}
            hint={t("dataStep.dropHint")}
            disabled={busy}
          />
          {workbook && (
            <div className="space-y-2">
              {workbookRoleSummary && !isAlertDismissed("workbook-role") && (
                <Alert className="border-primary/30 bg-primary/5 py-2">
                  <ListChecks className="h-4 w-4" />
                  <AlertTitle className="text-xs flex items-center justify-between">
                    {t("dataStep.roleTitle")}
                    <button type="button" onClick={() => dismissAlert("workbook-role")} className="text-muted-foreground hover:text-foreground">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </AlertTitle>
                  <AlertDescription className="text-[11px] leading-relaxed">
                    {workbookRoleSummary}
                  </AlertDescription>
                </Alert>
              )}
              <Label className="text-xs">{t("dataStep.sheetLabel")}</Label>
              <Select value={selectedSheet} onValueChange={setSelectedSheet}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {workbook.sheets.map((s) => {
                    const role = classifySheetRole(s);
                    const tag =
                      role === "data"
                        ? t("dataStep.roleData")
                        : role === "printTemplate"
                          ? t("dataStep.rolePrint")
                          : "";
                    return (
                      <SelectItem key={s.name} value={s.name}>
                        {s.name}{tag}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t("dataStep.colsRows", { cols: headers.length, rows: rows.length })}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Group data (data2): one chosen column → each group = one document */}
      <Card className="shadow-sm overflow-hidden" data-tour="tour-group-column">
        <CardHeader className="space-y-0 border-b bg-muted/30 px-3 py-2.5">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Layers className="h-4 w-4 text-primary" /> {t("dataStep.groupTitle")}
          </CardTitle>
          <CardDescription className="pt-0.5 text-[11px]">
            <span dangerouslySetInnerHTML={{ __html: t("dataStep.groupDesc") }} />
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 p-3">
          {!workbook ? (
            <div className="flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed p-6 text-center text-xs text-muted-foreground">
              <Layers className="h-5 w-5 opacity-50" />
              <p className="font-medium text-foreground">{t("dataStep.waitingData")}</p>
              <p>
                {t("dataStep.waitingDesc")}
              </p>
            </div>
          ) : (
            <>
              <SearchableSelect
                value={groupingColumns[0] || NO_MAPPING}
                options={primaryHeaders}
                emptyOption={{ value: NO_MAPPING, label: t("dataStep.noGroup") }}
                placeholder={t("dataStep.groupPlaceholder")}
                searchPlaceholder={t("dataStep.searchIn", { count: primaryHeaders.length })}
                onValueChange={(v) => {
                  const col = v === NO_MAPPING ? "" : v;
                  if (
                    enabledPages.length > 0 &&
                    col !== groupingColumns[0] &&
                    !window.confirm(t("dataStep.confirmChangeGroup"))
                  ) {
                    return;
                  }
                  setGroupingColumns(col ? [col] : []);
                  setGroupSearch("");
                  setPrintFilterMode(false);
                }}
              />
              {groupingColumns.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  <span dangerouslySetInnerHTML={{ __html: t("dataStep.groupExample") }} />
                </p>
              ) : (
                <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-2.5">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-800">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    {t("dataStep.rowsToDocs", { rows: rows.length, groups: groups.length })}
                    <span className="font-normal text-emerald-700/80">{t("dataStep.oneDocOnePage")}</span>
                  </div>
                  <div className="mt-2 space-y-1">
                    {groups.slice(0, 5).map((g) => (
                      <div
                        key={g.groupValue}
                        className="flex items-center justify-between gap-2 text-[11px]"
                      >
                        <span className="truncate font-medium text-emerald-900">{g.groupValue}</span>
                        <span className="shrink-0 text-emerald-700/80">{t("dataStep.rowsCount", { count: g.totalItems })}</span>
                      </div>
                    ))}
                    {groups.length > 5 && (
                      <p className="text-[10px] text-emerald-700/80">
                        {t("dataStep.moreDocs", { count: groups.length - 5 })}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Secondary-sheet lookup — part of the data step */}
      {workbook && workbook.sheets.length > 1 && (
        <Card className="lg:col-span-2 shadow-sm overflow-hidden">
          <CardHeader className="space-y-0 border-b bg-muted/30 px-3 py-2.5">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Layers className="h-4 w-4 text-primary" /> {t("dataStep.lookupTitle")}
            </CardTitle>
            <CardDescription className="text-xs">
              <span dangerouslySetInnerHTML={{ __html: t("dataStep.lookupDesc", { sheet: selectedSheet }) }} />
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={lookupSheet || NO_MAPPING}
                onValueChange={(v) => {
                  const sheetName = v === NO_MAPPING ? "" : v;
                  setLookupSheet(sheetName);
                  const sheet = workbook.sheets.find((s) => s.name === sheetName);
                  const suggested = sheet
                    ? suggestJoinKeys(primaryHeaders, sheet.headers)[0] ?? ""
                    : "";
                  setJoinKeyPrimary(suggested);
                  setJoinKeySecondary(
                    sheet && suggested ? counterpartColumn(suggested, sheet.headers) ?? "" : ""
                  );
                }}
              >
                <SelectTrigger className="h-9 w-56 text-xs">
                  <SelectValue placeholder={t("dataStep.noLookup")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_MAPPING}>{t("dataStep.noLookupItem")}</SelectItem>
                  {workbook.sheets
                    .filter((s) => s.name !== selectedSheet)
                    .map((s) => (
                      <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>

              {secondarySheet && (
                <>
                  <span className="text-xs text-muted-foreground">{t("dataStep.joinBy")}</span>
                  <Select value={joinKeyPrimary || NO_MAPPING} onValueChange={(v) => setJoinKeyPrimary(v === NO_MAPPING ? "" : v)}>
                    <SelectTrigger className="h-9 w-48 text-xs">
                      <SelectValue placeholder={t("dataStep.primaryCol")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_MAPPING}>{t("dataStep.noGroup")}</SelectItem>
                      {primaryHeaders.map((h) => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-muted-foreground">=</span>
                  <Select value={joinKeySecondary || NO_MAPPING} onValueChange={(v) => setJoinKeySecondary(v === NO_MAPPING ? "" : v)}>
                    <SelectTrigger className="h-9 w-48 text-xs">
                      <SelectValue placeholder={t("dataStep.secondaryCol")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_MAPPING}>{t("dataStep.noGroup")}</SelectItem>
                      {secondarySheet.headers.map((h) => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>

            {secondarySheet && joinKeyCandidates.length > 1 && (
              <p className="text-xs text-amber-600">
                {t("dataStep.joinCandidates", { count: joinKeyCandidates.length, cols: joinKeyCandidates.join(", ") })}
              </p>
            )}
            {secondarySheet && (!joinKeyPrimary || !joinKeySecondary) && (
              <p className="text-xs text-destructive">
                {t("dataStep.joinIncomplete")}
              </p>
            )}
            {joined.addedColumns.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {t("dataStep.joinedCols", { count: joined.addedColumns.length, cols: joined.addedColumns.join(", ") })}
              </p>
            )}
            {joinWarningMessages.map((msg, i) => (
              <p key={i} className="text-xs text-amber-600">⚠ {msg}</p>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="ent-footer-nav lg:col-span-2">
        <p className="text-[11px] text-muted-foreground">
          {workbook
            ? groupingColumns.length > 0
              ? t("dataStep.readyForTemplate")
              : t("dataStep.chooseGroup")
            : t("dataStep.uploadFirst")}
        </p>
        <Button
          data-tour="tour-continue-preview"
          disabled={!workbook || groupingColumns.length === 0}
          onClick={onContinue}
          className="gap-2 font-semibold shadow-sm"
        >
          {t("dataStep.continuePreview")} <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
